<script setup lang="ts">
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { scrubSentryEvent } from '@gh/common/utils'
import {
  probeSentryIngestFromPage,
  type SentryIngestProbeResult,
} from '@/utils/sentryIngestProbe'

/** Use this exact value in Sentry (Issues search or Discover filter). */
const SENTRY_SCRUB_TEST_TAG = 'gh-webapp-scrub-v1'

const PROBE_EMAIL = 'scrub.probe.user@example.com'
const PROBE_UID = 'K9sL2mN8pQr5tUv3wXy6zA1bC4dE7f'
const PROBE_UUID = '11111111-1111-4111-8111-111111111111'

const dsnConfigured = computed(() => Boolean(import.meta.env.VITE_SENTRY_DSN))
const ingestProbeRunning = ref(false)
const ingestProbeResult = ref<SentryIngestProbeResult | null>(null)
const lastRunId = ref<string | null>(null)
const lastEventId = ref<string | null>(null)
const isSending = ref(false)

/** Live preview: last refresh error (e.g. structuredClone failed). */
const livePreviewError = ref<string | null>(null)
const livePreviewUpdatedAt = ref<string | null>(null)
const liveClientSummary = ref<Record<string, unknown> | null>(null)
const liveScopeIsolationJson = ref('')
const liveScopeCurrentJson = ref('')
const liveSyntheticBeforeJson = ref('')
const liveSyntheticAfterJson = ref('')
const livePreviewTab = ref<'isolation' | 'current' | 'synthetic'>('synthetic')

const PREVIEW_MAX_DEPTH = 40

/**
 * Turn arbitrary SDK scope / context trees into JSON-safe data without throwing on
 * cycles, functions, DOM nodes, etc. Uses an ancestor chain so shared non-cyclic refs
 * still serialize twice (unlike a single WeakSet, which would falsely mark them "[Circular]").
 */
function toPreviewSerializable(
  value: unknown,
  ancestors: unknown[] = [],
  depth = 0,
): unknown {
  if (value === null || value === undefined) {
    return value
  }
  const t = typeof value
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return value
  }
  if (t === 'bigint') {
    return value.toString()
  }
  if (t === 'function') {
    return '[Function]'
  }
  if (t === 'symbol') {
    return '[Symbol]'
  }
  if (t !== 'object') {
    return String(value)
  }
  if (depth > PREVIEW_MAX_DEPTH) {
    return '[MaxDepth]'
  }
  if (ancestors.includes(value)) {
    return '[Circular]'
  }
  const chain = [...ancestors, value]

  if (value instanceof Date) {
    return value.toISOString()
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      ...(value.stack ? { stack: value.stack } : {}),
    }
  }
  if (typeof Element !== 'undefined' && value instanceof Element) {
    return `[Element ${value.tagName}]`
  }
  if (value instanceof RegExp) {
    return value.toString()
  }
  if (typeof Map !== 'undefined' && value instanceof Map) {
    if (value.size > 20) {
      return `[Map size=${value.size}]`
    }
    return {
      _type: 'Map',
      entries: Array.from(value.entries()).map(([k, v]) => [
        toPreviewSerializable(k, chain, depth + 1),
        toPreviewSerializable(v, chain, depth + 1),
      ]),
    }
  }
  if (typeof Set !== 'undefined' && value instanceof Set) {
    if (value.size > 20) {
      return `[Set size=${value.size}]`
    }
    return {
      _type: 'Set',
      values: Array.from(value.values()).map((v) =>
        toPreviewSerializable(v, chain, depth + 1),
      ),
    }
  }
  if (ArrayBuffer.isView(value) && !(value instanceof DataView)) {
    const v = value as ArrayBufferView
    return `[${v.constructor.name} len=${v.byteLength}]`
  }
  if (Array.isArray(value)) {
    return value.map((item) => toPreviewSerializable(item, chain, depth + 1))
  }

  const tag = Object.prototype.toString.call(value)
  if (tag !== '[object Object]') {
    return `[${tag.slice(8, -1)}]`
  }

  const out: Record<string, unknown> = {}
  for (const k of Object.keys(value as object)) {
    try {
      out[k] = toPreviewSerializable((value as Record<string, unknown>)[k], chain, depth + 1)
    } catch {
      out[k] = '[Unreadable]'
    }
  }
  return out
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(toPreviewSerializable(value), null, 2)
  } catch (e) {
    return JSON.stringify({ error: String(e) }, null, 2)
  }
}

function stripScopeForPreview(data: Record<string, unknown>): Record<string, unknown> {
  const {
    eventProcessors,
    attachments,
    span,
    sdkProcessingMetadata,
    ...rest
  } = data as Record<string, unknown> & {
    eventProcessors?: unknown[]
    attachments?: unknown[]
    span?: unknown
    sdkProcessingMetadata?: unknown
  }
  return {
    ...rest,
    _preview: {
      attachmentsCount: Array.isArray(attachments) ? attachments.length : 0,
      eventProcessorsCount: Array.isArray(eventProcessors) ? eventProcessors.length : 0,
      span: span != null ? '[Span]' : undefined,
      sdkProcessingMetadataKeys:
        sdkProcessingMetadata && typeof sdkProcessingMetadata === 'object'
          ? Object.keys(sdkProcessingMetadata as object)
          : [],
    },
  }
}

function mergeDeep(
  base: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(base ?? {}) }
  for (const [k, v] of Object.entries(next ?? {})) {
    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      typeof out[k] === 'object' &&
      out[k] !== null &&
      !Array.isArray(out[k])
    ) {
      out[k] = mergeDeep(out[k] as Record<string, unknown>, v as Record<string, unknown>)
    } else {
      out[k] = v as unknown
    }
  }
  return out
}

function buildSyntheticEventFromScopes(): Record<string, unknown> {
  const iso = Sentry.getIsolationScope().getScopeData()
  const cur = Sentry.getCurrentScope().getScopeData()

  const tags = { ...iso.tags, ...cur.tags }
  const extra = { ...iso.extra, ...cur.extra }
  const contexts = mergeDeep(
    iso.contexts as Record<string, unknown> | undefined,
    cur.contexts as Record<string, unknown> | undefined,
  )
  const user = cur.user ?? iso.user
  const breadcrumbs = [...(iso.breadcrumbs ?? []), ...(cur.breadcrumbs ?? [])]
  const transaction =
    (cur.transactionName as string | undefined) ??
    (iso.transactionName as string | undefined) ??
    undefined

  return {
    type: 'error',
    message: '[GH LIVE PREVIEW] Synthetic shell — not sent',
    tags,
    extra,
    contexts,
    user,
    breadcrumbs,
    transaction,
    request: {
      url: typeof window !== 'undefined' ? window.location.href : '',
    },
  }
}

function refreshLivePreview() {
  livePreviewError.value = null
  try {
    const client = Sentry.getClient()
    const opts = client?.getOptions()
    let dsnHost: string | undefined
    if (opts?.dsn) {
      try {
        dsnHost = new URL(String(opts.dsn)).host
      } catch {
        dsnHost = '[invalid dsn]'
      }
    }
    liveClientSummary.value = {
      dsnHost,
      environment: opts?.environment,
      release: opts?.release,
      sendDefaultPii: opts?.sendDefaultPii,
      firebaseProject: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    }

    const isoRaw = Sentry.getIsolationScope().getScopeData() as unknown as Record<string, unknown>
    const curRaw = Sentry.getCurrentScope().getScopeData() as unknown as Record<string, unknown>
    liveScopeIsolationJson.value = safeStringify(stripScopeForPreview(isoRaw))
    liveScopeCurrentJson.value = safeStringify(stripScopeForPreview(curRaw))

    const synthetic = buildSyntheticEventFromScopes()
    const plain = toPreviewSerializable(synthetic) as Record<string, unknown>
    liveSyntheticBeforeJson.value = JSON.stringify(plain, null, 2)
    const scrubbed = structuredClone(plain) as Record<string, unknown>
    scrubSentryEvent(scrubbed)
    liveSyntheticAfterJson.value = JSON.stringify(scrubbed, null, 2)

    livePreviewUpdatedAt.value = new Date().toISOString()
  } catch (e) {
    livePreviewError.value = e instanceof Error ? e.message : String(e)
  }
}

async function copyLiveAfter() {
  try {
    await navigator.clipboard.writeText(liveSyntheticAfterJson.value)
  } catch {
    // ignore
  }
}

function buildTags() {
  const runId = `run-${Date.now()}`
  lastRunId.value = runId
  return {
    sanitization_test: SENTRY_SCRUB_TEST_TAG,
    sanitization_test_run: runId,
  } as const
}

function buildPayload() {
  return {
    extra: {
      probe_email: PROBE_EMAIL,
      probe_customer_uid: PROBE_UID,
      probe_uuid: PROBE_UUID,
      phone: '5551234567',
      nested: { email: PROBE_EMAIL, customerId: PROBE_UID },
    },
    contexts: {
      scrub_probe: {
        url_like: `https://app.test/admin/customers/${PROBE_UID}/profile`,
        note: 'If scrubbing works, raw email / uid / uuid / phone should not appear as sent.',
      },
    },
  }
}

/** Message event — easiest to inspect (Extras + Contexts in Sentry). */
async function sendMessageProbe() {
  if (!dsnConfigured.value) return
  isSending.value = true
  lastEventId.value = null
  try {
    const eventId = Sentry.captureMessage(
      '[GH SCRUB TEST] Message probe — verify extras/contexts/tags below in Sentry',
      {
        level: 'info',
        tags: { ...buildTags() },
        ...buildPayload(),
      },
    )
    lastEventId.value = eventId ?? null
    await Sentry.flush(2000)
  } finally {
    isSending.value = false
  }
}

/**
 * Exception event — stack + extra. Error text is generic so we only assert on structured fields.
 */
async function sendExceptionProbe() {
  if (!dsnConfigured.value) return
  isSending.value = true
  lastEventId.value = null
  try {
    const err = new Error(
      '[GH SCRUB TEST] Exception probe — inspect exception payload + extra/contexts only',
    )
    const eventId = Sentry.captureException(err, {
      level: 'info',
      tags: { ...buildTags() },
      ...buildPayload(),
    })
    lastEventId.value = eventId ?? null
    await Sentry.flush(2000)
  } finally {
    isSending.value = false
  }
}

/** Breadcrumb + message — verifies breadcrumb message/data scrub in beforeSend. */
async function sendBreadcrumbThenMessage() {
  if (!dsnConfigured.value) return
  isSending.value = true
  lastEventId.value = null
  try {
    const tags = { ...buildTags() }
    Sentry.addBreadcrumb({
      category: 'gh.scrub_test',
      level: 'info',
      message: `Synthetic breadcrumb mentioning ${PROBE_EMAIL} and uid ${PROBE_UID}`,
      data: {
        customerId: PROBE_UID,
        email: PROBE_EMAIL,
        uuid: PROBE_UUID,
      },
    })
    const eventId = Sentry.captureMessage(
      '[GH SCRUB TEST] After breadcrumb — open Breadcrumbs on this event',
      {
        level: 'info',
        tags,
        ...buildPayload(),
      },
    )
    lastEventId.value = eventId ?? null
    await Sentry.flush(2000)
  } finally {
    isSending.value = false
  }
}

const sentryFilterHint = computed(
  () => `sanitization_test:"${SENTRY_SCRUB_TEST_TAG}"`,
)

async function copyFilter() {
  try {
    await navigator.clipboard.writeText(sentryFilterHint.value)
  } catch {
    // ignore
  }
}

async function copyRunId() {
  if (!lastRunId.value) return
  try {
    await navigator.clipboard.writeText(lastRunId.value)
  } catch {
    // ignore
  }
}

async function runIngestProbe() {
  ingestProbeRunning.value = true
  try {
    ingestProbeResult.value = await probeSentryIngestFromPage()
  } finally {
    ingestProbeRunning.value = false
  }
}
</script>

<template>
  <div class="space-y-6 text-left">
    <p class="text-sm text-base-content/80">
      Sends <strong>deliberately fake</strong> PII-shaped data through the real SDK so
      <code class="text-xs bg-base-200 px-1 rounded">beforeSend</code> scrubbing runs. Use a dev/staging
      Sentry project. Tag values use
      <code class="text-xs bg-base-200 px-1 rounded">{{ SENTRY_SCRUB_TEST_TAG }}</code> so you can filter
      human-readable events away from production noise.
    </p>

    <div v-if="!dsnConfigured" class="alert alert-warning text-sm">
      <span
        ><code>VITE_SENTRY_DSN</code> is not set — events will not be sent. Configure env for this
        build.</span
      >
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-4">
      <h4 class="font-semibold text-base mb-2">Ingest connectivity (not scrub)</h4>
      <p class="text-sm text-base-content/80 mb-3">
        Sends a minimal envelope directly to your configured ingest host. If this fails with a CORS /
        null-status console error while the SW is active, the cause is usually Firefox Tracking
        Protection or an ad blocker — not SW proxying (the worker returns early for
        <code class="text-xs bg-base-200 px-1 rounded">*.sentry.io</code>).
      </p>
      <button
        type="button"
        class="btn btn-sm btn-outline"
        :disabled="!dsnConfigured || ingestProbeRunning"
        @click="runIngestProbe"
      >
        {{ ingestProbeRunning ? 'Probing…' : 'Probe Sentry ingest' }}
      </button>
      <div
        v-if="ingestProbeResult"
        class="mt-3 text-sm rounded p-3"
        :class="ingestProbeResult.ok ? 'bg-success/10' : 'bg-warning/10'"
      >
        <p>
          <strong>{{ ingestProbeResult.ok ? 'OK' : 'Failed' }}</strong>
          <span v-if="ingestProbeResult.status != null"> — HTTP {{ ingestProbeResult.status }}</span>
        </p>
        <p class="text-xs mt-1 text-base-content/70">
          SW controlling page: {{ ingestProbeResult.swControlled ? 'yes' : 'no' }} ·
          crossOriginIsolated: {{ ingestProbeResult.crossOriginIsolated ? 'yes' : 'no' }}
        </p>
        <p class="text-xs mt-2 break-all">{{ ingestProbeResult.detail }}</p>
        <p v-if="ingestProbeResult.ingestUrl" class="text-xs mt-2 break-all opacity-70">
          {{ ingestProbeResult.ingestUrl }}
        </p>
      </div>
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-4">
      <h4 class="font-semibold text-base mb-2">Live client data (this tab only)</h4>
      <p class="text-sm text-base-content/80 mb-3">
        Reads
        <code class="text-xs bg-base-200 px-1 rounded">getIsolationScope()</code>
        and
        <code class="text-xs bg-base-200 px-1 rounded">getCurrentScope()</code>, builds a
        <strong>synthetic event shell</strong> (tags, extra, contexts, user, breadcrumbs, transaction,
        request URL), then runs the same
        <code class="text-xs bg-base-200 px-1 rounded">scrubSentryEvent</code> used in
        <code class="text-xs">beforeSend</code>. Nothing here is uploaded — it is only for comparison on
        the page.
      </p>
      <div class="flex flex-wrap gap-2 mb-3">
        <button type="button" class="btn btn-sm btn-outline" @click="refreshLivePreview">
          Refresh live preview
        </button>
        <button
          type="button"
          class="btn btn-sm btn-ghost"
          :disabled="!liveSyntheticAfterJson"
          @click="copyLiveAfter"
        >
          Copy scrubbed JSON
        </button>
      </div>
      <p v-if="livePreviewUpdatedAt" class="text-xs text-base-content/60 mb-2">
        Updated {{ livePreviewUpdatedAt }}
      </p>
      <div v-if="livePreviewError" class="alert alert-error text-sm mb-2">
        {{ livePreviewError }}
      </div>
      <div v-if="liveClientSummary" class="mb-3">
        <p class="text-xs font-semibold text-base-content/70 mb-1">Client summary</p>
        <pre
          class="text-xs bg-base-200 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap break-all"
          >{{ safeStringify(liveClientSummary) }}</pre
        >
      </div>
      <div class="tabs tabs-boxed mb-2 flex-wrap">
        <button
          type="button"
          class="tab"
          :class="{ 'tab-active': livePreviewTab === 'synthetic' }"
          @click="livePreviewTab = 'synthetic'"
        >
          Synthetic → scrub
        </button>
        <button
          type="button"
          class="tab"
          :class="{ 'tab-active': livePreviewTab === 'isolation' }"
          @click="livePreviewTab = 'isolation'"
        >
          Isolation scope
        </button>
        <button
          type="button"
          class="tab"
          :class="{ 'tab-active': livePreviewTab === 'current' }"
          @click="livePreviewTab = 'current'"
        >
          Current scope
        </button>
      </div>
      <div v-show="livePreviewTab === 'synthetic'" class="grid gap-3 md:grid-cols-2">
        <div>
          <p class="text-xs font-semibold mb-1">Before scrub (local only)</p>
          <pre
            class="text-xs bg-base-200 rounded p-2 overflow-auto max-h-80 whitespace-pre-wrap break-all"
            >{{ liveSyntheticBeforeJson || 'Click “Refresh live preview”.' }}</pre
          >
        </div>
        <div>
          <p class="text-xs font-semibold mb-1">After scrub (matches beforeSend output)</p>
          <pre
            class="text-xs bg-base-200 rounded p-2 overflow-auto max-h-80 whitespace-pre-wrap break-all"
            >{{ liveSyntheticAfterJson || '—' }}</pre
          >
        </div>
      </div>
      <div v-show="livePreviewTab === 'isolation'">
        <pre
          class="text-xs bg-base-200 rounded p-2 overflow-auto max-h-96 whitespace-pre-wrap break-all"
          >{{ liveScopeIsolationJson || 'Click “Refresh live preview”.' }}</pre
        >
      </div>
      <div v-show="livePreviewTab === 'current'">
        <pre
          class="text-xs bg-base-200 rounded p-2 overflow-auto max-h-96 whitespace-pre-wrap break-all"
          >{{ liveScopeCurrentJson || '—' }}</pre
        >
      </div>
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-4">
      <h4 class="font-semibold text-base mb-2">Repeatable check (Sentry UI)</h4>
      <ol class="list-decimal list-inside text-sm space-y-2 text-base-content/90">
        <li>
          Open
          <strong>Issues</strong>
          (or Discover) in the same
          <strong>environment</strong> as this app (
          <code class="text-xs bg-base-200 px-1 rounded">VITE_FIREBASE_PROJECT_ID</code>).
        </li>
        <li>
          Search or set filter:
          <code class="text-xs bg-base-200 px-1 rounded break-all">{{ sentryFilterHint }}</code>
          <button type="button" class="btn btn-xs btn-ghost ml-1" @click="copyFilter">Copy</button>
        </li>
        <li>Send a probe below, wait a few seconds, open the newest matching event.</li>
        <li>
          Confirm
          <strong>Extras / Contexts / Breadcrumbs</strong> do
          <strong>not</strong> contain raw
          <code class="text-xs">{{ PROBE_EMAIL }}</code>, the long uid, full UUID, or full phone; tags
          <code class="text-xs">sanitization_test*</code> should still be readable.
        </li>
      </ol>
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-4">
      <h4 class="font-semibold text-base mb-3">Probe payloads (sent in extras — should be scrubbed)</h4>
      <ul class="text-xs font-mono space-y-1 text-base-content/80 break-all">
        <li>email: {{ PROBE_EMAIL }}</li>
        <li>uid (28 chars): {{ PROBE_UID }}</li>
        <li>uuid: {{ PROBE_UUID }}</li>
        <li>phone key: 5551234567</li>
      </ul>
    </div>

    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="!dsnConfigured || isSending"
        @click="sendMessageProbe"
      >
        Send message probe
      </button>
      <button
        type="button"
        class="btn btn-outline btn-sm"
        :disabled="!dsnConfigured || isSending"
        @click="sendExceptionProbe"
      >
        Send exception probe
      </button>
      <button
        type="button"
        class="btn btn-outline btn-sm"
        :disabled="!dsnConfigured || isSending"
        @click="sendBreadcrumbThenMessage"
      >
        Breadcrumb + message
      </button>
    </div>

    <div v-if="lastRunId || lastEventId" class="text-sm space-y-1">
      <p v-if="lastRunId">
        Last run id:
        <code class="bg-base-200 px-1 rounded">{{ lastRunId }}</code>
        <button type="button" class="btn btn-xs btn-ghost" @click="copyRunId">Copy</button>
        — filter
        <code class="text-xs bg-base-200 px-1 rounded">sanitization_test_run:{{ lastRunId }}</code>
      </p>
      <p v-if="lastEventId" class="text-base-content/70">
        Sentry client event id (if returned):
        <code class="text-xs">{{ lastEventId }}</code>
      </p>
    </div>
  </div>
</template>
